import { useEffect, useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, RefreshCw, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { ImportSession } from '../../types/import';
import { importService } from '@/services/import/importService';
import { useImportStore } from '@/stores/importStore';
import { queryKeys } from '@/lib/query/queryKeys';
import { logger, LOG_CONTEXTS } from '@/utils/logger';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';

// Define provider type
interface Provider {
  has_sequence_numbers: boolean;
}

interface ImportProgressProps {
  session: ImportSession;
  providerId: number;
  versionId: number;
  onComplete?: () => void;
  onError?: (message: string) => void;
}

export function ImportProgress({ session, providerId, versionId, onComplete, onError }: ImportProgressProps) {
  const [, setRetryAttempts] = useState(0);
  const queryClient = useQueryClient();
  
  // Import store state and actions
  const { 
    updateProgress, 
    reset: resetData,
    setStep
  } = useImportStore();
  
  // Track session updates to prevent duplicate updates
  const processedSessionRef = useRef<{id: number, status: string, processed: number} | null>(null);
  
  // Add debug logging to track component lifecycle
  useEffect(() => {
    logger.info(LOG_CONTEXTS.IMPORT, 'ImportProgress component mounted', {
      sessionId: session?.id,
      initialTotal: session?.total_items,
      initialProcessed: session?.processed_items
    });
    
    return () => {
      logger.info(LOG_CONTEXTS.IMPORT, 'ImportProgress component unmounted');
    };
  }, [session]);

  // Polling interval for status updates - start with a slower interval
  const [interval, setInterval] = useState(2000);
  
  // Auto-adjust polling interval based on session status
  useEffect(() => {
    let newInterval = 2000; // Default 2 seconds
    
    if (session) {
      if (session.status === 'processing') {
        newInterval = 2000; // Slower polling even during processing to prevent too many updates
      } else if (session.status === 'completed' || 
                 session.status === 'failed' ||
                 session.status === 'completed_with_errors') {
        newInterval = 0; // Stop polling after completion
      } else {
        newInterval = 3000; // Even slower for other states
      }
      
      if (newInterval !== interval) {
        setInterval(newInterval);
      }
    }
  }, [session?.status, interval]);

  // Enhanced query management with reduced refetch rate
  const { data: currentSession, error: sessionError } = useQuery({
    queryKey: queryKeys.import.session(session.id),
    queryFn: () => importService.getSession(session.id),
    refetchInterval: interval,
    enabled: interval > 0,
    staleTime: 0,
    refetchOnWindowFocus: false, // Prevent additional fetches on window focus
  });

  // Add debug logging for session updates
  useEffect(() => {
    if (currentSession) {
      logger.info(LOG_CONTEXTS.IMPORT, 'Session data updated', {
        sessionId: currentSession.id,
        totalItems: currentSession.total_items,
        processedItems: currentSession.processed_items,
        status: currentSession.status,
        errorCount: currentSession.error_log?.length || 0
      });
    }
  }, [currentSession]);

  // Update progress only when session changes significantly
  useEffect(() => {
    if (!currentSession) return;
    
    // Check if this update is significantly different from previous update
    const currentSessionState = {
      id: currentSession.id,
      status: currentSession.status,
      processed: currentSession.processed_items
    };
    
    // Skip update if nothing significant has changed
    const prevState = processedSessionRef.current;
    if (prevState && 
        prevState.id === currentSessionState.id && 
        prevState.status === currentSessionState.status && 
        prevState.processed === currentSessionState.processed) {
      return;
    }
    
    // Update our ref with current state
    processedSessionRef.current = currentSessionState;
    
    const newProgress = {
      total: currentSession.total_items,
      processed: currentSession.processed_items,
      success: currentSession.processed_items - (currentSession.error_log?.length || 0),
      failed: currentSession.error_log?.length || 0,
      errors: currentSession.error_log || []
    };
    
    logger.info(LOG_CONTEXTS.IMPORT, 'Updating progress in ImportProgress', {
      total: newProgress.total,
      processed: newProgress.processed, 
      success: newProgress.success,
      failed: newProgress.failed
    });
    
    // Update progress in store
    updateProgress(newProgress);
    
  }, [currentSession?.id, currentSession?.processed_items, currentSession?.status, currentSession?.total_items, currentSession?.error_log, updateProgress]);

  // Retry mutation with proper cache handling
  const retryMutation = useMutation({
    mutationFn: () => importService.resumeFailedImport(session.id),
    onSuccess: () => {
      setRetryAttempts(prev => prev + 1);
      queryClient.invalidateQueries({
        queryKey: queryKeys.import.session(session.id)
      });
      toast.info("Retrying failed imports...");
    },
    onError: (error: Error) => {
      toast.error(error.message);
      onError?.(error.message);
    }
  });

  // Handle errors
  if (sessionError) {
    const errorMessage = sessionError instanceof Error ? sessionError.message : 'Failed to fetch import status';
    onError?.(errorMessage);
  }

  // Enhanced cache invalidation on completion - with protection against repeated calls
  useEffect(() => {
    // Use current session data but protect against repeated calls
    if (!currentSession) return;
    
    const isCompleted = 
      currentSession.status === 'completed' || 
      currentSession.status === 'completed_with_errors';
      
    const alreadyProcessed = processedSessionRef.current?.status === currentSession.status;
    
    if (isCompleted && !alreadyProcessed) {
      // Mark this session state as processed
      processedSessionRef.current = {
        id: currentSession.id,
        status: currentSession.status,
        processed: currentSession.processed_items
      };
      
      // Use a function for the refresh to avoid capturing state in closure
      const refreshData = async () => {
        // Use a more aggressive approach to ensure UI refreshes
        logger.info(LOG_CONTEXTS.IMPORT, 'Refreshing data after import completion', {
          providerId,
          versionId
        });
        
        try {
          // Check if this was a sequential provider import
          const provider = await queryClient.fetchQuery<Provider | null>({
            queryKey: ['provider-details', providerId],
            queryFn: async () => {
              const { data } = await supabase
                .from('providers')
                .select('has_sequence_numbers')
                .eq('id', providerId)
                .single();
              return data as Provider | null;
            },
            staleTime: 0
          });
          
          // For sequential providers, explicitly refresh sequence numbers
          if (provider?.has_sequence_numbers) {
            try {
              // Try to fix any data issues that might have occurred during import
              const { error: fixError } = await supabase.rpc('fix_channel_data_issues', {
                p_provider_id: providerId,
                p_version_id: versionId
              });
              
              if (fixError) {
                logger.error(LOG_CONTEXTS.IMPORT, 'Error fixing channel data', { error: fixError });
              } else {
                logger.info(LOG_CONTEXTS.IMPORT, 'Successfully fixed any channel data issues');
              }
              
              await importService.refreshSequenceNumbers(providerId, versionId);
              logger.info(LOG_CONTEXTS.IMPORT, 'Successfully refreshed sequence numbers after import');
            } catch (error) {
              logger.error(LOG_CONTEXTS.IMPORT, 'Error refreshing sequence numbers', { error });
            }
          }

          // First completely remove cached data
          queryClient.removeQueries({
            queryKey: queryKeys.channels.all
          });
          queryClient.removeQueries({
            queryKey: queryKeys.channelNumbers.all
          });
          
          // Wait a bit before invalidating to ensure DB operations are complete
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // For sequential providers, do a more aggressive refresh
          if (provider?.has_sequence_numbers) {
            try {
              // Force a hard reset of the query client cache
              queryClient.clear();
              logger.info(LOG_CONTEXTS.IMPORT, 'Cleared query cache for sequential provider');
              
              // Execute a clean fetch to establish fresh data
              await queryClient.fetchQuery({
                queryKey: queryKeys.channels.byVersion(providerId, versionId),
                queryFn: async () => {
                  const { data } = await supabase
                    .from('channels')
                    .select(`
                      *,
                      numbers:channel_numbers(*)
                    `)
                    .eq('provider_id', providerId)
                    .eq('version_id', versionId);
                  return data;
                }
              });
              
              logger.info(LOG_CONTEXTS.IMPORT, 'Successfully refetched channel data');
            } catch (error) {
              logger.error(LOG_CONTEXTS.IMPORT, 'Error in direct channel refetch', { error });
            }
          } else {
            // Then perform a stronger invalidation by:
            // 1. Force refetching directly
            // 2. Then invalidating with refetchType=all
            
            // First direct force refetch
            try {
              await queryClient.fetchQuery({
                queryKey: queryKeys.channels.byVersion(providerId, versionId),
                queryFn: async () => {
                  const { data } = await supabase
                    .from('channels')
                    .select(`
                      *,
                      numbers:channel_numbers(*)
                    `)
                    .eq('provider_id', providerId)
                    .eq('version_id', versionId);
                  return data;
                },
                staleTime: 0
              });
              logger.info(LOG_CONTEXTS.IMPORT, 'Direct force refetch complete');
            } catch (error) {
              logger.error(LOG_CONTEXTS.IMPORT, 'Error in direct force refetch', { error });
            }
          }
          
          // Then invalidate all queries to force a complete refresh
          await Promise.all([
            // Force refetch all channel-related data
            queryClient.invalidateQueries({
              queryKey: queryKeys.channels.all,
              refetchType: 'all'
            }),
            queryClient.invalidateQueries({
              queryKey: queryKeys.channelNumbers.all, 
              refetchType: 'all'
            }),
            // Also specifically invalidate version-specific queries
            queryClient.invalidateQueries({
              queryKey: queryKeys.channels.byVersion(providerId, versionId),
              refetchType: 'all'
            }),
            queryClient.invalidateQueries({
              queryKey: queryKeys.channelNumbers.byVersion(providerId, versionId),
              refetchType: 'all'
            })
          ]);
          
          // Add a small delay before completing to allow queries to refetch
          await new Promise(resolve => setTimeout(resolve, 300));
          
          logger.info(LOG_CONTEXTS.IMPORT, 'Cache invalidation complete');
        } catch (error) {
          logger.error(LOG_CONTEXTS.IMPORT, 'Error refreshing data', { error });
        }

        // Call the onComplete handler
        onComplete?.();
      };

      // Start the refresh with a short delay
      setTimeout(refreshData, 500);
    }
  }, [currentSession?.status, currentSession?.id, providerId, versionId, queryClient, onComplete]);

  // Function to reset and start over
  const handleReset = () => {
    resetData();
    setStep('form');
  };

  // Helper function to check if import is complete
  const isImportComplete = (session: ImportSession) => {
    return ['completed', 'failed', 'completed_with_errors'].includes(session.status);
  };

  // Calculate progress percentage
  const getProgressPercentage = () => {
    if (!currentSession?.total_items) return 0;
    if (currentSession.total_items === 0) return 100;
    return Math.round((currentSession.processed_items / currentSession.total_items) * 100);
  };

  // Define status color based on session status
  const getStatusColor = () => {
    switch (currentSession?.status) {
      case 'completed':
        return 'bg-green-500';
      case 'completed_with_errors':
        return 'bg-amber-500';
      case 'failed':
        return 'bg-red-500';
      case 'pending':
        return 'bg-yellow-500';
      case 'processing':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  };

  if (!currentSession) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between text-sm">
          <span>Loading import status...</span>
        </div>
        <Progress value={0} />
      </div>
    );
  }

  const isComplete = isImportComplete(currentSession);
  const hasErrors = (currentSession.error_log?.length || 0) > 0;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Import Progress</h3>
          <p className="text-sm text-muted-foreground">
            Tracking your channel import progress
          </p>
        </div>
        <div className="flex space-x-2">
          {isComplete && (
            <Button 
              variant="default" 
              size="sm" 
              onClick={handleReset}
            >
              Start New Import
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-medium">Session ID: {currentSession.id}</p>
                <div className="flex items-center space-x-2 mt-1">
                  <div className={`w-3 h-3 rounded-full ${getStatusColor()}`} />
                  <span className="text-sm capitalize">Status: {currentSession.status.replace('_', ' ')}</span>
                </div>
              </div>
              <Badge variant={currentSession.status === 'completed' ? 'default' : 'outline'}>
                {currentSession.processed_items} of {currentSession.total_items} channels processed
              </Badge>
            </div>

            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm">{getProgressPercentage()}% Complete</span>
                <span className="text-sm">
                  <span className="text-green-600">{currentSession.processed_items - (currentSession.error_log?.length || 0)} successful</span> · 
                  <span className="text-red-600 ml-1">{currentSession.error_log?.length || 0} failed</span>
                </span>
              </div>
              <Progress value={getProgressPercentage()} className="h-2" />
            </div>

            {hasErrors && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Import Errors</AlertTitle>
                <AlertDescription>
                  <div className="mt-2 max-h-60 overflow-y-auto">
                    <ul className="list-disc pl-4 space-y-1">
                      {currentSession.error_log?.slice(0, 10).map((error, index) => (
                        <li key={index} className="text-sm">
                          {error.message || "Unknown error"}
                          {error.line ? ` (Line ${error.line})` : ''}
                        </li>
                      ))}
                      {(currentSession.error_log?.length || 0) > 10 && (
                        <li className="text-sm">
                          ...and {(currentSession.error_log?.length || 0) - 10} more errors
                        </li>
                      )}
                    </ul>
                  </div>
                  
                  {currentSession.status !== 'failed' && (
                    <div className="mt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => retryMutation.mutate()}
                        disabled={retryMutation.isPending}
                        className="flex items-center gap-2"
                      >
                        <RefreshCw className={`h-3 w-3 ${retryMutation.isPending ? 'animate-spin' : ''}`} />
                        Retry Failed Items
                      </Button>
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {isComplete && currentSession.status === 'completed' && !hasErrors && (
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertTitle>Import Complete</AlertTitle>
                <AlertDescription>
                  All channels have been successfully imported.
                </AlertDescription>
              </Alert>
            )}
            
            {isComplete && (
              <div className="flex justify-end pt-2">
                <Button onClick={handleReset}>
                  {currentSession.status === 'completed' && !hasErrors ? 'Start New Import' : 'Try Again'}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

